import React, { Suspense, lazy } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useSearchParams,
} from 'react-router-dom';
import { Layout } from './components/Layout';
import { CityRedirect } from './components/CityRedirect';
import { CookieConsent } from './components/CookieConsent';
import { GoogleAnalytics } from './components/GoogleAnalytics';
import { RegionProvider } from './context/RegionContext';
import { TimeProvider } from './context/TimeContext';
import { AppPage } from './pages/AppPage';
import { TeamPage } from './pages/TeamPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { SupportPage } from './pages/SupportPage';
import { FAQPage } from './pages/FAQPage';
import './index.css';

// Lazy load the map page to keep other routes fast
const MapPage = lazy(() =>
  import('./pages/MapPage').then((module) => ({ default: module.MapPage }))
);

// The shared-bar route renders the same map, so it is split out too — the two
// share one chunk rather than pulling the map into the main bundle.
const SharedBarPage = lazy(() =>
  import('./pages/SharedBarPage').then((module) => ({ default: module.SharedBarPage }))
);

// Loading fallback for map page
const MapLoadingFallback = () => (
  <div className="flex items-center justify-center h-full bg-gray-50">
    <div className="text-center">
      <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
      <p className="text-gray-500 text-sm">Loading map...</p>
    </div>
  </div>
);

/**
 * The original share-link shape, `/centmap/detail?id=<barId>`.
 *
 * Nothing generates it any more — every platform now shares
 * `/centmap/bar/<barId>` — but links in this form were shared from iOS before
 * the shapes were unified and will keep being followed indefinitely. Without
 * this they fell through to the catch-all and dropped the recipient on the map's
 * front door with no sign of the bar they were sent.
 *
 * `replace` so the canonical URL is what ends up in history and in the address
 * bar, and the back button returns where the user came from.
 */
const LegacyBarLinkRedirect: React.FC = () => {
  const [params] = useSearchParams();
  const barId = params.get('id');

  if (!barId) return <Navigate to="/" replace />;
  return <Navigate to={`/centmap/bar/${encodeURIComponent(barId)}`} replace />;
};

function App() {
  return (
    <BrowserRouter>
      <RegionProvider>
        <TimeProvider>
          <Routes>
            {/* Root redirects to detected/default city */}
            <Route path="/" element={<CityRedirect />} />

            {/* City-specific map routes */}
            <Route element={<Layout />}>
              <Route
                path="/:city"
                element={
                  <Suspense fallback={<MapLoadingFallback />}>
                    <MapPage />
                  </Suspense>
                }
              />

              {/* Shared bar link, and the universal-link target for both apps.
                  public/.well-known/apple-app-site-association and
                  public/.well-known/assetlinks.json both claim /centmap/*, so on
                  a phone with Centmap installed this URL opens the app instead
                  of the browser — iPhone or Android. This page still has to
                  stand on its own: the same link is followed on desktops and on
                  phones without the app. */}
              <Route
                path="/centmap/bar/:barId"
                element={
                  <Suspense fallback={<MapLoadingFallback />}>
                    <SharedBarPage />
                  </Suspense>
                }
              />

              {/* Legacy shape, redirected onto the canonical one above. */}
              <Route path="/centmap/detail" element={<LegacyBarLinkRedirect />} />

              <Route path="/app" element={<AppPage />} />
              <Route path="/team" element={<TeamPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/support" element={<SupportPage />} />
              <Route path="/faq" element={<FAQPage />} />
            </Route>

            {/* Anything else — a stale share link, a mistyped path — lands on
                the map rather than on a blank page. */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>

          {/* After <Routes> on purpose: effects run in tree order, so by the
              time the pageview fires the route has already set document.title
              and GA records the right one. */}
          <GoogleAnalytics />

          <CookieConsent />
        </TimeProvider>
      </RegionProvider>
    </BrowserRouter>
  );
}

export default App;
